import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { makeStyles } from 'tss-react/mui'
import React, { useEffect, useState } from 'react'
import { Divider, Grid, Typography, Box } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TabButtonGroup } from 'src/Components/TabButton'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { v4 as uuidv4 } from 'uuid'
import Joi from 'joi'
import { useValidation } from '@e3dc-react/shell/Hooks/useValidation/useValidation'
import { isUndefined } from 'lodash'
import useAvailableLines from 'src/Libs/system-charts/shared/hooks/useAvailableLines'
import moment from '@e3dc-react/shell/Libs/moment'
import { ChartType, ChartView, CustomTrace, LoadingState } from 'src/Libs/chart-kit/shared/utils/chartModel'
import Chart, { ChartProps } from '../../shared/components/Chart'
import { useFetchData } from 'src/Libs/system-data-handler/api/SystemDataFetching'
import { addCustomSumsToData } from '../utils/add-custom-sums-to-data'
import TraceEditor from './TraceEditor'
import Sums from './Sums'
import CurrentTraces from './CurrentTraces'
import AvailableTraces from './AvailableTraces'
import Views from './Views'
import { useXAxisTicks } from '../../cartesian-charts'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { UnitTypes } from 'src/Libs/types'
import { DEFAULT_HUMAN_TIME } from '@e3dc-react/shell/Libs/date'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

const traceSchema = Joi.object().keys({
  color: Joi.string().required(),
  key: Joi.string().required(),
  sumKeys: Joi.alternatives().conditional('key', { is: 'Custom_Sum', then: Joi.array().min(1).required(), otherwise: Joi.forbidden() }),
})

const stateSchema = Joi.object().keys({
  name: Joi.string().required(),
  currentTraces: Joi.array().items(traceSchema).min(1).required(),
})

const useStyles = makeStyles()(theme => ({
  legendWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: theme.spacing(2),
    width: '100%',
  },
  tracesTitle: {
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(0),
  },
  viewTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  chartViews: {
    paddingBottom: theme.spacing(4),
  },
  title: {
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  chartWrapper: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
  },
}))

export interface ChartSettings {
  traces: CustomTrace[]
  views: Record<ChartView, ChartType> | undefined
  name?: string
  summaryUnit?: string
  id?: string
  isInitial?: boolean
  maxHeight?: number
  installationId: number
  storageSerialNumber: string
}

interface CustomChart {
  name: string
  currentTraces: CustomTrace[]
}

export interface SystemChartEditorProps {
  place: number
  onClose: () => void
  chartId?: number
  chartProps?: ChartProps
  views?: Record<ChartView, ChartType> | undefined
  name?: string
  handleSaveChart: (props: ChartSettings) => void
  serialNumber?: string
  loadingState?: LoadingState
  installationId: number
}

const SystemChartEditor: React.FC<SystemChartEditorProps> = ({
  chartId,
  place,
  onClose,
  chartProps,
  views,
  name,
  handleSaveChart,
  serialNumber,
  loadingState,
  installationId,
}) => {
  const { classes } = useStyles()
  const { triggerAppRating } = useAppRatingContext()
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [currentTraces, setCurrentTraces] = useState<CustomTrace[]>(chartProps?.customTraces ?? [])
  const [selectedTrace, setSelectedTrace] = useState<CustomTrace>()
  const { t } = useCustomTranslation()
  const [chartViews, setChartViews] = useState<Record<ChartView, ChartType> | undefined>(views)
  const [chartType, setChartType] = useState<ChartType>(chartProps?.type ?? 'line')
  const [currentChart, setCurrentChart] = useState<CustomChart>({ name: name ?? '', currentTraces })
  const [from] = useState(moment().startOf('day'))
  const [to] = useState(moment().startOf('day').add(1, 'day'))
  const rechartData = useFetchData({ storageId: serialNumber, from, to, view: 'day' })
  const [currentRechartData, setCurrentRechartData] = useState(chartProps?.rechartData ?? rechartData)
  const availableLines = useAvailableLines()
  const [validationActive, setValidationActive] = useState(false)
  const validation = useValidation(currentChart, stateSchema)
  const chartHeight = 400
  const { ticks: xAxisTicks } = useXAxisTicks(from, to, chartProps?.view ?? 'day')

  const setTraces = (action: CustomTrace[] | React.SetStateAction<CustomTrace[]>): void => {
    const newTraces = typeof action === 'function' ? action(currentTraces) : action
    setCurrentTraces(newTraces)
    setCurrentChart(chart => ({ ...chart, currentTraces: newTraces }))
  }

  const handlePopoverOpen = (traceKey: string, event?: React.MouseEvent<Element, MouseEvent>): void => {
    const trace = currentTraces.find(trace => trace.alternateKey === traceKey) || currentTraces.find(trace => trace.key === traceKey)
    if (event) setAnchorEl(event.currentTarget)
    setSelectedTrace(trace)
  }

  useEffect(() => {
    //if this is a chart with custom Sums created by the user, add entries to data with those sum values
    const customSums = currentTraces?.filter(trace => trace.sumKeys?.length)

    const data = chartProps?.rechartData ?? rechartData
    addCustomSumsToData(data, customSums)
    setCurrentRechartData({ ...data })
  }, [chartProps?.rechartData, currentTraces, rechartData])

  const handlePopoverClose = (): void => {
    setAnchorEl(null)
  }

  const validationObject = validationActive ? validation : {}

  const disabled =
    !isUndefined(installationId) || Object.keys(validationObject).length > 0 || currentTraces.filter(trace => trace.sumKeys && !trace.name).length > 0

  const summaryUnitToUse = chartProps?.summaryUnit ?? currentTraces.find(trace => trace.summaryUnit)?.summaryUnit ?? UnitTypes.wattHour

  const dialogActions = {
    primary: {
      name: t('common.save'),
      onClick: () => {
        setValidationActive(true)

        if (Object.keys(validation).length > 0 || isUndefined(serialNumber)) {
          return
        }
        handleSaveChart?.({
          id: !isUndefined(chartId) ? String(chartId) : uuidv4(),
          traces: currentTraces,
          views: chartViews,
          name: chartProps?.isInitial ? undefined : currentChart.name,
          summaryUnit: summaryUnitToUse,
          installationId,
          storageSerialNumber: serialNumber,
        })
        onClose()
        triggerAppRating()
      },
      disabled,
    },
    secondary: [{ name: t('common.cancel') }],
  }

  const errorMessages = Object.keys(validationObject).map(key => {
    if (key === 'name') return t('system-data-handler.editor.error.name-missing')

    if (key === 'currentTraces') return t('system-data-handler.editor.error.traces-min')

    if (key.startsWith('currentTraces.')) {
      return t('system-data-handler.editor.error.custom-sum-traces')
    }

    return null
  })

  return (
    <Dialog
      place={place}
      onClose={onClose}
      title={isUndefined(chartId) ? t('systemDataHandler.editor.addChart') : t('systemDataHandler.editor.editChart')}
      actions={dialogActions}
      additionalActionsLeft={
        errorMessages.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 1 }}>
            {errorMessages.map((message, index) => {
              if (!message) return null

              return <ErrorMessage key={`error-${index}`} title={message} />
            })}
          </Box>
        )
      }
    >
      <Box>
        <TraceEditor
          anchorEl={anchorEl}
          currentTraces={currentTraces}
          handlePopoverClose={handlePopoverClose}
          selectedTrace={selectedTrace?.alternateKey ?? selectedTrace?.key}
          setCurrentTraces={setTraces}
          onlyColor={chartProps?.type === 'pie'}
          withName={!!selectedTrace?.sumKeys}
        />
        {!chartProps?.isInitial && (
          <TextField
            label={t('systemDataHandler.editor.chartName')}
            onChange={value => setCurrentChart(chart => ({ ...chart, name: value }))}
            value={currentChart.name}
            required
            className={classes.title}
            helperText={validationObject['name'] ? validationObject['name'].message : ''}
          />
        )}
        <div className={classes.chartWrapper}>
          <Chart
            from={from}
            type={chartType}
            unit={chartProps?.unit ?? UnitTypes.watt}
            secondUnit={chartProps?.secondUnit}
            xAxisDomain={chartProps?.xAxisDomain ?? [from.valueOf(), to.valueOf()]}
            xAxisFormat={chartProps?.xAxisFormat ?? DEFAULT_HUMAN_TIME}
            xAxisInterval={chartProps?.xAxisInterval}
            rechartData={currentRechartData}
            customTraces={currentTraces}
            height={chartHeight}
            impossibleTraces={chartProps?.impossibleTraces ?? []}
            showForecast={!!chartProps?.rechartData && chartProps?.showForecast}
            traceNames={{}}
            view={chartProps?.view || 'day'}
            loadingState={loadingState}
            summaryUnit={summaryUnitToUse}
            xAxisTicks={chartProps?.xAxisTicks ?? xAxisTicks}
            hasBrush={false}
          />
        </div>
        <Divider />
        {chartViews && chartProps?.type !== 'pie' && (
          <div className={classes.chartViews}>
            <div className={classes.viewTitleContainer}>
              <Typography variant="h3">{t('systemDataHandler.editor.viewsTitle')}</Typography>
              <TabButtonGroup
                options={[
                  { label: t('systemDataHandler.editor.previewLines'), onChange: () => setChartType('line'), id: 'chart-type-line' },
                  { label: t('systemDataHandler.editor.previewBars'), onChange: () => setChartType('bar'), id: 'chart-type-bar' },
                ]}
                active={chartType === 'bar' ? 1 : 0}
                width="50%"
              />
            </div>

            <Grid container spacing={4} justifyContent="space-between">
              <Views chartViews={chartViews} setChartViews={setChartViews} />
            </Grid>
          </div>
        )}
        {!chartProps?.isInitial && (
          <>
            <Divider />
            <Box sx={theme => ({ display: 'flex', alignItems: 'center', paddingBottom: theme.spacing(2) })}>
              <Typography className={classes.tracesTitle}>{t('systemDataHandler.editor.sums')}</Typography>
              <Typography variant="subtitle2">{t('systemDataHandler.editor.sumsHelperText')}</Typography>
              <Typography variant="subtitle2" sx={theme => ({ fontWeight: 'bold', paddingLeft: theme.spacing(2) })}>
                {t('systemDataHandler.editor.sumsHelperExample')}
              </Typography>
            </Box>
            <Sums chartType={chartType} currentTraces={currentTraces} setCurrentTraces={setTraces} handlePopoverOpen={handlePopoverOpen} />
          </>
        )}
        <Divider />
        {currentTraces.length > 0 && (
          <>
            <Typography className={classes.tracesTitle} variant="h3">
              {t('systemDataHandler.editor.editActiveTraces')}
            </Typography>
            <div className={classes.legendWrapper}>
              <CurrentTraces
                chartType={chartType}
                currentTraces={currentTraces}
                setCurrentTraces={setTraces}
                chartProps={chartProps}
                handlePopoverOpen={handlePopoverOpen}
              />
            </div>
          </>
        )}
        {!chartProps?.isInitial && availableLines && (
          <>
            <Divider />
            <Typography className={classes.tracesTitle}>{t('systemDataHandler.editor.availableTraces')}</Typography>
            <div className={classes.legendWrapper}>
              <AvailableTraces chartType={chartType} availableLines={availableLines} currentTraces={currentTraces} setCurrentTraces={setTraces} />
            </div>
          </>
        )}
      </Box>
    </Dialog>
  )
}

export default SystemChartEditor
